import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { apiClient } from '../lib/api-client';

/** Duck-typed check (avoids vitest mock stripping the class). */
function isUnauthorized(err: unknown): boolean {
  return err instanceof Error && err.name === 'UnauthorizedError';
}

interface UseApiResult<T> {
  data: T | null;
  error: string | null;
  isLoading: boolean;
  needsAuth: boolean;
  refetch: () => void;
}

export function useApi<T>(
  fetcher: (client: typeof apiClient, token?: string) => Promise<T>,
  deps: unknown[] = []
): UseApiResult<T> {
  const { token, isLoading: authLoading } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = useCallback(() => {
    setRefetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    // Wait for auth to finish (e.g. code exchange in progress)
    if (authLoading) return;

    // Don't call authenticated APIs without a token
    if (!token) {
      setIsLoading(false);
      setNeedsAuth(true);
      return;
    }
    setNeedsAuth(false);

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetcher(apiClient, token)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (isUnauthorized(err)) {
            setNeedsAuth(true);
            setError(null);
          } else {
            setError(err instanceof Error ? err.message : 'Unknown error');
          }
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authLoading, refetchCount, ...deps]);

  return { data, error, isLoading, needsAuth, refetch };
}
